import { useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ThumbsUp, MessageSquareText, Loader2, X } from "@/components/icons";

/**
 * Ícones de reação e comentário do gestor em uma atividade.
 * - Só gestor/diretor vê os botões.
 * - Contadores são sempre visíveis (a quem tem acesso à lista).
 * - Não altera status da atividade.
 */
export default function AtividadeReacoes({ atividade, onChange, testId }) {
  const { user } = useAuth();
  const managerish = ["admin", "director", "manager", "diretor_geral"].includes(user?.role);
  const [busy, setBusy] = useState(false);
  const [marcado, setMarcado] = useState(!!atividade.meu_reconhecimento);
  const [countRec, setCountRec] = useState(atividade.reconhecimentos_count || 0);
  const [countCom, setCountCom] = useState(atividade.comentarios_count || 0);
  const [comOpen, setComOpen] = useState(false);
  const [comText, setComText] = useState("");

  const toggleRec = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/atividades/${atividade.id}/reconhecer`);
      setMarcado(!!data.meu_reconhecimento);
      setCountRec(data.reconhecimentos_count);
      setCountCom(data.comentarios_count);
      onChange && onChange({ ...atividade, meu_reconhecimento: data.meu_reconhecimento, reconhecimentos_count: data.reconhecimentos_count, comentarios_count: data.comentarios_count });
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const enviarComentario = async () => {
    const t = comText.trim();
    if (!t) return;
    setBusy(true);
    try {
      await api.post(`/atividades/${atividade.id}/comentario`, { text: t });
      toast.success("Comentário enviado");
      setComText("");
      setComOpen(false);
      const novo = countCom + 1;
      setCountCom(novo);
      onChange && onChange({ ...atividade, comentarios_count: novo });
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const showBotoes = managerish;
  const temContadores = countRec > 0 || countCom > 0;

  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      {temContadores && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid={`${testId}-contadores`}>
          {countRec > 0 && <span className="inline-flex items-center gap-1"><ThumbsUp className="w-3.5 h-3.5" /> {countRec}</span>}
          {countCom > 0 && <span className="inline-flex items-center gap-1"><MessageSquareText className="w-3.5 h-3.5" /> {countCom}</span>}
        </div>
      )}
      {showBotoes && (
        <div className={`flex items-center gap-1 transition-opacity ${temContadores ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus-within:opacity-100"}`}>
          <button
            onClick={toggleRec}
            disabled={busy}
            data-testid={`${testId}-recognize`}
            title={marcado ? "Remover reconhecimento" : "Reconhecer"}
            className={`p-1.5 rounded-lg border transition-colors ${marcado ? "bg-brand-soft border-brand text-brand" : "border-input bg-white text-slate-500 hover:bg-slate-50"}`}
          >
            <ThumbsUp className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setComOpen((v) => !v); }}
            disabled={busy}
            data-testid={`${testId}-comment-btn`}
            title="Comentar"
            className={`p-1.5 rounded-lg border transition-colors ${comOpen ? "bg-brand-soft border-brand text-brand" : "border-input bg-white text-slate-500 hover:bg-slate-50"}`}
          >
            <MessageSquareText className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {comOpen && (
        <div className="absolute right-4 top-full mt-1 z-20 w-72 bg-card border border-black/10 rounded-xl shadow-lg p-3" onClick={(e) => e.stopPropagation()} data-testid={`${testId}-popover`}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-navy">Comentário do gestor</div>
            <button onClick={() => setComOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
          </div>
          <textarea
            value={comText}
            onChange={(e) => setComText(e.target.value)}
            rows={3}
            autoFocus
            placeholder="Ex.: Ótimo follow-up, cliente aqueceu."
            data-testid={`${testId}-comment-input`}
            className="w-full px-3 py-2 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40"
          />
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => { setComOpen(false); setComText(""); }} className="h-8 px-3 rounded-lg border border-input text-xs">Cancelar</button>
            <button
              onClick={enviarComentario}
              disabled={busy || !comText.trim()}
              data-testid={`${testId}-comment-send`}
              className="h-8 px-3 rounded-lg bg-brand text-white text-xs font-semibold hover:bg-brand-hover disabled:opacity-50 flex items-center gap-1.5"
            >
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Enviar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
